import { useCallback, useState } from "react";
import {
  cancelJob, cancelTestRun, listAllTestRuns, listOpenJobs,
  type QaJobSummary, type QaTestRunSummary,
} from "../../api/qaOrchestrator";
import { relativeTime } from "./memberModel";
import { usePolling, type PollContext } from "../../hooks/usePolling";
import { StatePill, useAction, type SetError } from "./qaShared";

// Everything the orchestrator is doing or did lately, across every
// repository: test runs, and the work still waiting.

const isOpen = (s: string) => s === "pending" || s === "running";

export default function QaActivitySection({ setError }: { setError: SetError }) {
  const [runs, setRuns] = useState<QaTestRunSummary[] | null>(null);
  const [jobs, setJobs] = useState<QaJobSummary[]>([]);
  const reload = useCallback(async () => {
    const [r, j] = await Promise.all([listAllTestRuns(), listOpenJobs()]);
    setRuns(r);
    setJobs(j);
  }, []);
  const { busy, confirmThen, dialog } = useAction(reload, setError);
  // Only the first load's error is shown; a refresh that fails is tried again.
  const poll = useCallback(({ first }: PollContext) => reload().catch((e) => { if (first) setError((e as Error).message); }), [reload, setError]);
  usePolling(poll, 15_000);

  const cancelNote = "Waiting work is dropped. A plan already running on a runner can't be stopped and finishes.";

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>Test runs</h3>
          <p>The latest test runs of every repository, newest first. Each project's QA page shows its own with every result.</p>
        </div>
      </div>
      {runs === null ? (
        <div className="members-empty">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="members-empty">No test runs yet.</div>
      ) : (
        <div className="qo-table-wrap">
          <table className="qo-table">
            <thead><tr><th>Repository</th><th>Status</th><th>Plans</th><th>Started by</th><th>Started</th><th /></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.repo}</strong><small>{r.ref} @ {r.sha.slice(0, 12)}{r.kind === "capture" ? " · re-baseline" : ""}</small></td>
                  <td><StatePill state={r.status} /></td>
                  <td>{r.plans.length}<small>{Object.entries(r.counts).map(([k, n]) => `${n} ${k}`).join(" · ")}</small></td>
                  <td>{r.trigger ? <>{r.trigger.event}{r.trigger.prNumber ? ` #${r.trigger.prNumber}` : ""}<small>{r.trigger.actor}</small></> : <span className="qo-muted">—</span>}</td>
                  <td>{relativeTime(r.createdAt)}</td>
                  <td className="qo-row-actions">
                    {isOpen(r.status) && <button className="btn btn-ghost" disabled={busy} onClick={() => void confirmThen({ title: `Cancel the run of ${r.repo}?`, body: cancelNote, confirmLabel: "Cancel run", cancelLabel: "Keep it" }, () => cancelTestRun(r.id))}>Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="qo-section-head qo-spaced">
        <div>
          <h3>Queue</h3>
          <p>Plans waiting for a runner or running now, including ones sent straight to the API.</p>
        </div>
      </div>
      <div className="qo-table-wrap">
        <table className="qo-table">
          <thead><tr><th>Plan</th><th>Status</th><th>Runner</th><th>Queued</th><th /></tr></thead>
          <tbody>
            {jobs.map((j) => {
              const last = j.runs.at(-1);
              return (
                <tr key={j.id}>
                  <td><strong>{j.plan ?? j.kind}</strong><small><code className="qo-id">{j.id}</code></small></td>
                  <td><StatePill state={last?.status ?? j.status} /></td>
                  <td>{last?.computerName ?? <span className="qo-muted">not yet</span>}</td>
                  <td>{relativeTime(j.createdAt)}</td>
                  <td className="qo-row-actions"><button className="btn btn-ghost" disabled={busy} onClick={() => void confirmThen({ title: `Cancel ${j.plan ?? j.id}?`, body: cancelNote, confirmLabel: "Cancel it", cancelLabel: "Keep it" }, () => cancelJob(j.id))}>Cancel</button></td>
                </tr>
              );
            })}
            {jobs.length === 0 && <tr><td colSpan={5} className="qo-muted">Nothing waiting.</td></tr>}
          </tbody>
        </table>
      </div>

      {dialog}
    </section>
  );
}
